import type { Resource } from '../../types';
import { logger } from '../../utils/logger';
import { mapApiTypeToDisplayName } from '../../utils/resourceTypeMapper';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';
import { USE_MOCK_DIVISIONS } from '../Organization/fetchDivisionsFromService';

/** Mock resources keyed by API resource type string. Shown when USE_MOCK_DIVISIONS is true. */
const MOCK_RESOURCES: Record<string, Array<{ id: string; name: string }>> = {
  architect_flow:              [{ id: 'flow-001', name: 'Main IVR Flow' }, { id: 'flow-002', name: 'After Hours Flow' }, { id: 'flow-003', name: 'Emergency Flow' }],
  flow_milestone:              [{ id: 'fm-001', name: 'Milestone: Greeted' }, { id: 'fm-002', name: 'Milestone: Escalated' }],
  flow_outcome:                [{ id: 'fo-001', name: 'Outcome: Resolved' }, { id: 'fo-002', name: 'Outcome: Transferred' }],
  architect_schedules:         [{ id: 'sched-001', name: 'Business Hours' }, { id: 'sched-002', name: 'Holiday Schedule' }],
  architect_schedulegroups:    [{ id: 'sg-001', name: 'Standard Hours Group' }],
  architect_datatable:         [{ id: 'dt-001', name: 'Customer Lookup Table' }, { id: 'dt-002', name: 'Product Codes' }],
  architect_datatable_with_data: [{ id: 'dtd-001', name: 'Customer Lookup Table (with Data)' }],
  architect_emergencygroup:    [{ id: 'eg-001', name: 'Storm Emergency Group' }],
  architect_ivr:               [{ id: 'ivr-001', name: 'Main Call Route' }, { id: 'ivr-002', name: 'Support Call Route' }],
  routing_queue:               [{ id: 'q-001', name: 'Sales Queue' }, { id: 'q-002', name: 'Support Queue' }, { id: 'q-003', name: 'Demo Queue' }],
  routing_wrapupcode:          [{ id: 'wu-001', name: 'Resolved' }, { id: 'wu-002', name: 'Follow Up Required' }, { id: 'wu-003', name: 'Transferred' }],
  outbound_campaign:           [{ id: 'camp-001', name: 'Summer Campaign' }, { id: 'camp-002', name: 'Demo Campaign' }],
  outbound_contact_list:       [{ id: 'cl-001', name: 'Prospects List' }, { id: 'cl-002', name: 'Renewals List' }],
  outbound_dnclist:            [{ id: 'dnc-001', name: 'National DNC' }, { id: 'dnc-002', name: 'Internal DNC' }],
  outbound_messagingcampaign:  [{ id: 'mc-001', name: 'SMS Outreach Campaign' }],
  script:                      [{ id: 'scr-001', name: 'Agent Script' }, { id: 'scr-002', name: 'Outbound Script' }],
  external_contacts_contact:   [{ id: 'ec-001', name: 'John Smith' }, { id: 'ec-002', name: 'Jane Doe' }],
  external_contacts_organization: [{ id: 'eo-001', name: 'Acme Corp' }, { id: 'eo-002', name: 'Demo Client Ltd' }],
  user:                        [{ id: 'usr-001', name: 'Alice Johnson' }, { id: 'usr-002', name: 'Bob Williams' }],
};

export const fetchResourcesFromService = async (
  bearerToken: string,
  orgName: string,
  resourceType: string
): Promise<Resource[]> => {
  if (USE_MOCK_DIVISIONS) {
    const displayType = mapApiTypeToDisplayName(resourceType);
    const mockItems = MOCK_RESOURCES[resourceType] ?? [];
    logger.info('[MOCK] Returning mock resources', { resourceType, displayType, count: mockItems.length });
    const mapped: Resource[] = mockItems.map((item) => ({ id: item.id, name: item.name, type: displayType }));
    return Promise.resolve(mapped);
  }

  const baseUrl = getEnv().VITE_EDCI_API_URL;
  const url = `${baseUrl}/api/genesysInfo/${orgName}/${resourceType}`;

  try {
    const response = await authenticatedFetch(url, {
      headers: getV2AuthHeaders(bearerToken)
    });

    if (!response.ok) {
      logger.error('Fetch resources API returned error', {
        status: response.status,
        statusText: response.statusText,
        organization: orgName,
        resourceType,
        url
      });
      throw new Error(`Error fetching resources: ${response.status} ${response.statusText}`);
    }

    // API returns array directly, not wrapped in entities
    const data: Resource[] = await response.json();
    logger.debug('Resources fetched successfully', {
      organization: orgName,
      resourceType,
      count: data.length
    });

    // Map the API response to your Resource interface
    return data.map((resource: Resource) => {
      const mapped: Resource = {
        id: resource.id || '',
        name: resource.name || '',
        type: mapApiTypeToDisplayName(resource.type) || resourceType
      };
      if (resource.division !== undefined) {
        mapped.division = resource.division;
      }
      return mapped;
    });
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to fetch resources', {
      error: msg,
      organization: orgName,
      resourceType,
      url
    });
    throw error;
  }
};

export default fetchResourcesFromService;
